// 組織設定 › IT 監控 › 輪值表（SPEC #kinio-kms-notify「輪值表」；k1-wbs M1-11 / M1-14 最小版：單一時段、週次輪替）。
// 多時段、代班、跳段提示、送達檢查（「收不到推播」）留到 M2（M2-07）；值班主管欄位先存。
// 寫入要 step-up（403 REAUTH_REQUIRED → 重新驗證，api.ts）；存檔後 kmgr 通知指定它的家戶所屬組織（防止靜默）。
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { orgApi, type MyOrg, type OrgMember } from '../../lib/planeApi';
import { browserTz, validTz } from '../../lib/tz';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { onDutyNow, rotationAt, slotCoversWeek, thisMonday, validateSchedule } from './model';
import { errText, NS, StateBox } from './parts';
import type { OncallSchedule, OncallSlot } from './types';

const DAY_KEYS = [1, 2, 3, 4, 5, 6, 7];

export function useOrgMembers(org: MyOrg) {
  return useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org), staleTime: 60_000 });
}

export function memberName(members: OrgMember[] | undefined, id: string | null | undefined): string {
  if (!id) return '';
  const m = members?.find((x) => x.user_id === id);
  return m?.display_name || m?.email || id;
}

export function useSchedules(org: MyOrg) {
  return useQuery({ queryKey: ['kmgr', 'schedules', org.org_id], queryFn: () => kmgrApi.schedules(org.api, org.org_id), retry: 1 });
}

export function OncallList({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const q = useSchedules(org);
  const members = useOrgMembers(org);
  const now = new Date();
  if (q.isLoading) return <SkeletonLines n={3} label={t('common.loading')} />;
  if (q.isError)
    return (
      <StateBox icon="⊘" title={t('oncall.unavailable')} body={errText(t, q.error)}>
        <button className="btn" onClick={() => void q.refetch()}>
          {t('common.retry')}
        </button>
      </StateBox>
    );
  const list = q.data ?? [];
  return (
    <div data-testid="km-oncall-list">
      <div className="km-block-head">
        <p className="muted" style={{ margin: 0 }}>
          {t('oncall.intro')}
        </p>
        <span className="spacer" />
        <Link className="btn btn-primary" to="/org/kmgr/oncall/new">
          {t('oncall.new')}
        </Link>
      </div>
      {list.length === 0 ? (
        <StateBox icon="○" title={t('oncall.none')} body={t('oncall.none_body')} testId="km-oncall-empty" />
      ) : (
        <div className="card">
          <table className="km-table">
            <thead>
              <tr>
                <th>{t('oncall.name')}</th>
                <th>{t('oncall.now_on')}</th>
                <th>{t('oncall.supervisor')}</th>
                <th>{t('oncall.used_by')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const d = onDutyNow(s, now);
                return (
                  <tr key={s.schedule_id} data-schedule={s.schedule_id}>
                    <td>
                      <Link to={`/org/kmgr/oncall/${encodeURIComponent(s.schedule_id)}`}>{s.name}</Link>
                    </td>
                    <td>{d.person ? memberName(members.data, d.person) : <span className="km-gap">{t('oncall.nobody_now', { name: memberName(members.data, d.supervisor) })}</span>}</td>
                    <td>{memberName(members.data, s.supervisor)}</td>
                    <td className="tabular">{s.home_count === undefined ? '—' : t('oncall.homes_n', { n: s.home_count })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function blankSlot(): OncallSlot {
  return { name: '', days: [...DAY_KEYS], start: '00:00', end: '00:00', rotation: [] };
}

export function OncallEditor({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const { scheduleId } = useParams();
  const isNew = !scheduleId || scheduleId === 'new';
  const q = useSchedules(org);
  const members = useOrgMembers(org);
  const existing = q.data?.find((s) => s.schedule_id === scheduleId);
  if (q.isLoading || members.isLoading) return <SkeletonLines n={5} label={t('common.loading')} />;
  if (!isNew && !existing)
    return (
      <StateBox icon="⊘" title={t('oncall.not_found')}>
        <Link className="btn" to="/org/kmgr/oncall">
          {t('oncall.back')}
        </Link>
      </StateBox>
    );
  return <EditorForm key={existing?.schedule_id ?? 'new'} org={org} existing={existing} members={(members.data ?? []).filter((m) => m.status === 'active' || !m.status)} />;
}

function EditorForm({ org, existing, members }: { org: MyOrg; existing?: OncallSchedule; members: OrgMember[] }) {
  const { t } = useTranslation(NS);
  const nav = useNavigate();
  const qc = useQueryClient();
  const tz0 = existing?.tz && validTz(existing.tz) ? existing.tz : browserTz();
  const [name, setName] = useState(existing?.name ?? '');
  const [tz, setTz] = useState(tz0);
  const [supervisor, setSupervisor] = useState(existing?.supervisor ?? '');
  const [slot, setSlot] = useState<OncallSlot>(existing?.slots[0] ?? blankSlot());
  const [start, setStart] = useState(existing?.rotation_start ?? thisMonday(tz0, new Date()));
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const name2 = (id: string | null | undefined) => memberName(members, id);
  const rot = useMemo(() => rotationAt(slot.rotation, start, tz, new Date()), [slot.rotation, start, tz]);
  const full = slotCoversWeek(slot);
  const setRotation = (r: string[]) => setSlot((s) => ({ ...s, rotation: r }));
  const move = (i: number, d: -1 | 1) => {
    const r = [...slot.rotation];
    const j = i + d;
    if (j < 0 || j >= r.length) return;
    [r[i], r[j]] = [r[j], r[i]];
    setRotation(r);
  };
  const toggleDay = (d: number) => setSlot((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d].sort() }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // 時段的值班主管沒填 = 用輪值表的（kmgr 不收 null，不送這個欄位）
    const { supervisor: slotSup, ...slotRest } = slot;
    const body = { name: name.trim(), tz, supervisor, slots: [{ ...slotRest, ...(slotSup ? { supervisor: slotSup } : {}), name: slot.name.trim() || t('oncall.slot_default') }], rotation_start: start };
    const p = validateSchedule(body);
    setProblems(p);
    if (p.length) return;
    setBusy(true);
    setErr(null);
    try {
      if (existing) await kmgrApi.putSchedule(org.api, org.org_id, existing.schedule_id, { ...body, expected_version: existing.version });
      else await kmgrApi.createSchedule(org.api, org.org_id, body);
      await qc.invalidateQueries({ queryKey: ['kmgr', 'schedules', org.org_id] });
      nav('/org/kmgr/oncall');
    } catch (x) {
      setErr(isKinioError(x) && x.code === 'CONFLICT' ? t('oncall.err_conflict') : errText(t, x));
    } finally {
      setBusy(false);
    }
  };
  const available = members.filter((m) => !slot.rotation.includes(m.user_id));
  return (
    <form onSubmit={(e) => void submit(e)} data-testid="km-oncall-editor" noValidate>
      {problems.length > 0 && (
        <div className="km-bar bad" role="alert">
          <span className="ic" aria-hidden>
            ✕
          </span>
          <span className="txt">{problems.map((p) => t(p)).join('；')}</span>
        </div>
      )}
      <section className="card" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="km-form">
          <label className="field">
            <span>{t('oncall.name')}</span>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-invalid={problems.includes('oncall.err_name')} />
          </label>
          <label className="field">
            <span>{t('oncall.tz')}</span>
            <input className="input" value={tz} onChange={(e) => setTz(e.target.value)} onBlur={() => !validTz(tz) && setTz(tz0)} list="km-tz-list" />
            <datalist id="km-tz-list">
              {['Asia/Taipei', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Hong_Kong', 'Asia/Singapore', 'UTC'].map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
          </label>
          <label className="field">
            <span>{t('oncall.supervisor')}</span>
            <select className="input" value={supervisor} onChange={(e) => setSupervisor(e.target.value)} aria-invalid={problems.includes('oncall.err_supervisor')}>
              <option value="">{t('oncall.pick')}</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {name2(m.user_id)}
                </option>
              ))}
            </select>
            <span className="hint">{t('oncall.supervisor_hint')}</span>
          </label>
          <label className="field">
            <span>{t('oncall.rotation_start')}</span>
            <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <span className="hint">{t('oncall.rotation_start_hint')}</span>
          </label>
        </div>
      </section>
      <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-slot-h">
        <h2 id="km-slot-h" style={{ fontSize: 16, marginTop: 0 }}>
          {t('oncall.slot_title')}
        </h2>
        <div className="km-form">
          <label className="field">
            <span>{t('oncall.slot_name')}</span>
            <input className="input" value={slot.name} placeholder={t('oncall.slot_default')} onChange={(e) => setSlot((s) => ({ ...s, name: e.target.value }))} />
          </label>
          <div className="field">
            <span>{t('oncall.time')}</span>
            <div className="toolbar" style={{ margin: 0 }}>
              <input className="input" type="time" aria-label={t('oncall.start')} value={slot.start} onChange={(e) => setSlot((s) => ({ ...s, start: e.target.value }))} />
              <span aria-hidden>–</span>
              <input className="input" type="time" aria-label={t('oncall.end')} value={slot.end} onChange={(e) => setSlot((s) => ({ ...s, end: e.target.value }))} />
            </div>
            <span className="hint">{t('oncall.time_hint')}</span>
          </div>
          <fieldset className="field span-2" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend>{t('oncall.days')}</legend>
            <div className="toolbar wrap" style={{ margin: 0 }}>
              {DAY_KEYS.map((d) => (
                <label key={d} className="km-check">
                  <input type="checkbox" checked={slot.days.includes(d)} onChange={() => toggleDay(d)} />
                  {t(`day.${d}`)}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        {!full && (
          <div className="km-bar warn" role="status" style={{ marginTop: 'var(--space-3)' }} data-testid="km-oncall-gap">
            <span className="ic" aria-hidden>
              ⚠
            </span>
            <span className="txt">{t('oncall.gap', { name: name2(supervisor) || t('oncall.supervisor') })}</span>
          </div>
        )}
        <h3 style={{ fontSize: 15 }}>{t('oncall.rotation')}</h3>
        <ol className="km-roster" data-testid="km-roster">
          {slot.rotation.map((u, i) => (
            <li key={u}>
              <span className="name">
                {name2(u)}
                {rot.current === u && <strong> · {t('oncall.this_week')}</strong>}
                {rot.next === u && rot.current !== u && <span className="muted"> · {t('oncall.next_week')}</span>}
              </span>
              <button type="button" className="btn btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t('oncall.up', { name: name2(u) })}>
                ↑
              </button>
              <button type="button" className="btn btn-sm" onClick={() => move(i, 1)} disabled={i === slot.rotation.length - 1} aria-label={t('oncall.down', { name: name2(u) })}>
                ↓
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setRotation(slot.rotation.filter((x) => x !== u))} aria-label={t('oncall.remove', { name: name2(u) })}>
                {t('oncall.remove_short')}
              </button>
            </li>
          ))}
        </ol>
        {slot.rotation.length === 0 && <p className="muted km-sub">{t('oncall.rotation_empty')}</p>}
        <div className="toolbar wrap" style={{ marginTop: 8 }}>
          <select className="input" style={{ maxWidth: 320 }} value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('oncall.add_person')}>
            <option value="">{t('oncall.pick')}</option>
            {available.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {name2(m.user_id)}
              </option>
            ))}
          </select>
          <button type="button" className="btn" disabled={!pick} onClick={() => (setRotation([...slot.rotation, pick]), setPick(''))}>
            {t('oncall.add')}
          </button>
        </div>
        <p className="km-sub" data-testid="km-oncall-summary">
          {rot.current ? t('oncall.summary', { slot: slot.name || t('oncall.slot_default'), now: name2(rot.current), sup: name2(supervisor) || '—', next: name2(rot.next) }) : t('oncall.summary_empty')}
        </p>
      </section>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="km-dock">
        <span className="why">{t('oncall.save_note')}</span>
        <Link className="btn" to="/org/kmgr/oncall">
          {t('common.cancel')}
        </Link>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? t('common.saving') : t('oncall.save')}
        </button>
      </div>
    </form>
  );
}
